import { CompositeFilterDescriptor, FilterDescriptor, filterBy } from '@progress/kendo-data-query';
import {
  DropDownList,
  DropDownListChangeEvent,
  DropDownListFilterChangeEvent,
} from '@progress/kendo-react-dropdowns';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SUPER_ADMIN_COMPANY_NAME, SUPER_ADMIN_ID } from 'src/constants/admin';
import useAuth from 'src/hooks/useAuth';
import useCompany from 'src/hooks/useCompany';
import usePermission from 'src/hooks/usePermissions';
import useCompanyInvoiceScansApi, {
  CompanyInvoiceScans,
} from 'src/services/useCompanyInvoiceScansApi';
import useCompanyReceiptScansApi, {
  CompanyReceiptScans,
} from 'src/services/useCompanyReceiptScansApi';
import 'src/style/select-company/SelectCompany.scss';
import { Companies, Company } from 'src/types/Companies';
import { SetState } from 'src/types/generic';
import { IsManagerPermissions } from '../constants/permissionsList';

interface CompanyProps {
  title?: string;
  className?: string;
  onChange?: (company: Company) => void;
  isInvoice?: boolean;
  isScanManager?: boolean;
  refreshCompany?: boolean;
  isControlled?: boolean;
  selectSuperAdmin?: boolean;
  setOcrCompanies?: SetState<CompanyInvoiceScans>;
}

const mergeArraysWithSimilarCompanyId = (
  array1: Companies,
  array2: CompanyInvoiceScans | CompanyReceiptScans,
  isInvoice: boolean
) => {
  const mergedArray = array1.map((item1) => {
    const matchingItem = array2.find(
      (item2) => Number(item2.companyId) === Number(item1.companyId)
    );
    return {
      ...item1,
      companyName: `${item1.companyName} (${matchingItem ? matchingItem.attachmentCount : 0}${
        isInvoice ? ' unused' : ''
      })`,
    };
  });
  return mergedArray;
};

const SelectCompany: FC<CompanyProps> = ({
  title,
  className,
  onChange,
  isInvoice = false,
  isScanManager = false,
  refreshCompany,
  isControlled = false,
  selectSuperAdmin = false,
  setOcrCompanies,
}) => {
  const { t } = useTranslation();
  const { getCompanyInvoiceScans, companyInvoiceScans } = useCompanyInvoiceScansApi(false);
  const { getCompanyReceiptScans, companyReceiptScans } = useCompanyReceiptScansApi(false);
  const { user, isSuperAdmin, companies } = useAuth();
  const [searchParams] = useSearchParams();
  const companyId = useMemo(() => searchParams.get('companyId') || '', [searchParams]);
  const { company: selectedCompany, changeCompany: setSelectedCompany } = useCompany();
  const isManager = usePermission({ permissions: IsManagerPermissions });
  const [data, setData] = useState(companies);

  // Create default item for the dropdown
  const defaultItem = useMemo(
    () => ({
      companyId: '',
      companyName: t('Select a Company...'),
    }),
    [t]
  );

  useEffect(() => {
    setData(companies);
  }, [companies]);

  const handleCompanyChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const { value } = e.target;
      if (!isControlled) {
        setSelectedCompany(value);
      }
      onChange?.(value);
    },
    [isControlled, onChange, setSelectedCompany]
  );

  const filterData = (filter: FilterDescriptor | CompositeFilterDescriptor) => {
    const response = isInvoice ? companyInvoiceScans : companyReceiptScans;
    const data = mergeArraysWithSimilarCompanyId(companies, response, isInvoice).slice();
    return filterBy(data, filter);
  };

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    setData(filterData(event.filter));
  };

  const getScans = useCallback(async () => {
    const scansCall = isInvoice ? getCompanyInvoiceScans : getCompanyReceiptScans;
    const response = await scansCall();
    if (isInvoice) {
      setOcrCompanies?.(response as CompanyInvoiceScans);
    }
    setData(() => mergeArraysWithSimilarCompanyId(companies, response, isInvoice));
  }, [companies, getCompanyInvoiceScans, getCompanyReceiptScans, isInvoice, setOcrCompanies]);

  useEffect(() => {
    if (isScanManager) {
      getScans().catch((e) => {
        setOcrCompanies?.([]);
      });
    }
  }, [getScans, isScanManager, refreshCompany, setOcrCompanies]);

  useEffect(() => {
    if (selectSuperAdmin && isSuperAdmin)
      setSelectedCompany((prev) =>
        prev?.companyId
          ? prev
          : {
              companyName: SUPER_ADMIN_COMPANY_NAME,
              companyId: SUPER_ADMIN_ID,
            }
      );
    else if (companyId) {
      setSelectedCompany(data.filter((company) => company.companyId === companyId)[0]);
    } else if (companies.length === 1) {
      setSelectedCompany(companies.filter((company) => company.companyId)[0]);
    } else if (!isManager) {
      const company = data.filter((company) => company.companyId === user.CompanyId)[0];
      if (company) {
        setSelectedCompany(company);
      }
    }
  }, [
    companies,
    companyId,
    data,
    selectSuperAdmin,
    isManager,
    isSuperAdmin,
    setSelectedCompany,
    user.CompanyId,
  ]);

  const value = useMemo(
    () => data.find((company) => company.companyId === selectedCompany.companyId) ?? defaultItem,
    [selectedCompany.companyId, data, defaultItem]
  );

  return (
    <div
      className={`flex items-center ${
        companies?.length === 1 || !isManager ? 'hidden' : 'visible'
      } ${className}`}
    >
      <span className="text-sm font-bold mr-2 whitespace-nowrap">
        {title ? title : t('Select a Company') + ':'}
      </span>
      <div>
        <DropDownList
          id="view-drop-down-list"
          className="__selectCompanyDropdown w-md ml-2 bg-white"
          data={data}
          onChange={handleCompanyChange}
          value={value}
          textField="companyName"
          disabled={!isManager}
          dataItemKey="companyId"
          filterable
          onFilterChange={filterChange}
        />
      </div>
    </div>
  );
};

export default SelectCompany;
